import { popularList } from "@/constants/apiConstantData";
import { extractValues } from "@/helpers/helper";
import { getSiteProps } from "@/utils";
import trackClevertapEvents from "@/utils/trackClevertapEvents";
import { Toast } from "@monsterindia/global-ui-components";
import React, { useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";

import { MultiDropDownController } from "@/components/common/dropdownController";
import ProfileModal from "@/components/common/profileModal";
import { useUpdatePreference } from "@/components/profile/preferencesCard/query";

type DropdownOptionType = {
  id: string | number;
  text: string;
  selected?: boolean;
};

type JobPreferencesProps = {
  isEditing: boolean;
  closeEdit: () => void;
  isMobile: boolean;
  formRef: any;
  prefilledData?: any;
  onSave?: (updated: any) => void;
};

const JobPreferencesEditCard = ({
  isEditing,
  closeEdit,
  isMobile,
  formRef,
  prefilledData,
  onSave,
}: JobPreferencesProps) => {
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  const siteProps = getSiteProps(window.location.hostname);
  const channel = siteProps?.site_map || "rexmonster";

  const suggestedOptions = {
    list: popularList(channel).location,
    type: "Popular Locations",
  };
  const { locations, roles } = prefilledData?.jobPreference ?? {};
  const defaultValues = { locations: locations || [], roles: roles || [] };

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    clearErrors,
    getValues,
  } = useForm<FieldValues>({ defaultValues });

  const { mutate } = useUpdatePreference();

  const handleSaveBtnLoading = (val: boolean) => {
    setIsSaveLoading(val);
  };
  const onSubmit = (data: any) => {
    handleSaveBtnLoading(true);
    const payload = {
      jobPreference: {
        locations: extractValues(data.locations, ["id", "text"]),
        roles: extractValues(data.roles, ["id", "text"]),
      },
    };
    mutate(
      { data: payload, action: "edit" },
      {
        onSuccess: () => {
          handleSaveBtnLoading(false);
          setToast({
            show: true,
            message: "Job preferences updated successfully",
            type: "success",
          });
          onSave?.(payload);
          trackClevertapEvents.eventPush("ProfilePage_Section_Saved", {
            Job_Preferences_Saved: JSON.stringify(payload),
          });
          closeEdit();
        },
        onError: (err) => {
          handleSaveBtnLoading(false);
          setToast({
            show: true,
            message:
              err instanceof Error ? err.message : "Something went wrong.",
            type: "error",
          });
        },
      },
    );
  };
  const handleSelect = (name: string, value: DropdownOptionType) => {
    clearErrors(name);
    setValue(name, value);
  };
  useEffect(() => {
    if (prefilledData) {
      Object.entries(defaultValues).forEach(([key, value]) => {
        setValue(key, value);
      });
    }
  }, [prefilledData]);

  const renderForm = (
    <form
      className="flex h-full flex-col gap-6"
      onSubmit={handleSubmit(onSubmit)}
      ref={formRef}
    >
      <MultiDropDownController
        isRequired={true}
        name="roles"
        maxSelect={5}
        control={control}
        isSearchable={true}
        type="DESIGNATION"
        label="Preferred Role(s)"
        onSelect={(value) => handleSelect("roles", value)}
        errorMessage={errors.roles ? "This field is required" : ""}
        placeholder={
          getValues("roles") && getValues("roles").length >= 5
            ? "Maximum 5 preferred job titles can be selected"
            : "Enter or select your preferred job title"
        }
      />

      <MultiDropDownController
        isRequired={true}
        name="locations"
        maxSelect={5}
        control={control}
        isSearchable={true}
        type="LOCATION"
        label="Preferred Location(s)"
        onSelect={(value) => handleSelect("locations", value)}
        errorMessage={errors.locations ? "This field is required" : ""}
        suggestedOptions={suggestedOptions}
        placeholder={
          getValues("locations") && getValues("locations").length >= 5
            ? "Maximum 5 preferred locations can be selected"
            : "Enter or select your preferred location"
        }
      />
    </form>
  );
  return (
    <>
      <Toast
        position="top-center"
        showToast={toast.show}
        text={toast.message}
        type={toast.type}
        width={isMobile ? "75" : "30"}
        timer={3000}
        onClose={() => setToast((prev) => ({ ...prev, show: false }))}
      />
      <ProfileModal
        id="preference-edit-modal"
        showModal={isEditing}
        closeModal={closeEdit}
        isMobile={isMobile}
        customClass="h-[80vh]"
        showCloseButton={true}
        screenAlignment={isMobile ? "bottom-sheet" : "center"}
        showHandleBar={isMobile}
        heading="Job Preferences"
        footerPrimaryBtnLabel="Save"
        footerPrimaryBtnHandler={() => formRef.current?.requestSubmit()}
        footerPrimaryBtnIsLoading={isSaveLoading}
      >
        {renderForm}
      </ProfileModal>
    </>
  );
};

export default JobPreferencesEditCard;
